import { useCallback, useContext, useRef } from 'react';
import type { MouseEvent, KeyboardEvent } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { AppNavigationContext } from '@confluence/app-navigation-context';
import {
	useCanCreateSpace,
	useCreateSpaceStoreActions,
} from '@confluence/create-space/entry-points/createSpace.ts';
import { useHardStorageEnforcement } from '@confluence/storage-enforcement/entry-points/HardEnforcement/useHardStorageEnforcement.ts';

//  Opens the space creation drawer if user has space create permissions

export const useCreateSpaceDrawer = () => {
	const { canCreateSpace } = useCanCreateSpace();
	const { resetStickySearchRef } = useContext(AppNavigationContext);
	const createSpaceButtonRef = useRef<HTMLElement | null>(null);
	const { openDrawer } = useCreateSpaceStoreActions();
	const { enforceStorageLimit } = useHardStorageEnforcement({
		source: 'create-space-button',
	});
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const openSpaceCreationDrawer = useCallback(
		(e: MouseEvent | KeyboardEvent) => {
			resetStickySearchRef?.current?.();
			openDrawer(createSpaceButtonRef, 'personalizedTemplatesV2ExperimentSetupTeamSpaceCard');

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'createSpaceButton',
					source: 'experiment-personalized-templates-v2',
					attributes: {
						location: 'personalizedTemplatesV2ExperimentSetupTeamSpaceCard',
					},
				},
			}).fire();
			e.stopPropagation();
		},
		[resetStickySearchRef, openDrawer, createAnalyticsEvent],
	);

	const handleSetupTeamSpaceClick = useCallback(
		(e: MouseEvent<Element> | KeyboardEvent<Element>) =>
			enforceStorageLimit(openSpaceCreationDrawer)(e),
		[enforceStorageLimit, openSpaceCreationDrawer],
	);

	return {
		canCreateSpace,
		handleSetupTeamSpaceClick,
	};
};
